import { OmsProduct } from "../lusca-search-api/generated-src";
import type { OmsFilterDraft } from "./useOmsFilterCriteria";

const storageKey = (product: OmsProduct) => `oms-export-draft:${product}`;
const limit = 256 * 1024;
type Check = (value: unknown) => boolean;

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const string: Check = (value) => typeof value === "string";
const boolean: Check = (value) => typeof value === "boolean";
const count: Check = (value) =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;
const timestamp: Check = (value) =>
  typeof value === "string" && Number.isFinite(Date.parse(value));
const strings: Check = (value) => Array.isArray(value) && value.every(string);
const oneOf =
  (...values: unknown[]): Check =>
  (value) =>
    values.includes(value);

function optional(
  value: Record<string, unknown>,
  key: string,
  check: Check
): boolean {
  return value[key] === undefined || check(value[key]);
}

function filters(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.length <= 20 &&
    value.every(
      (item) =>
        record(item) &&
        string(item.column) &&
        string(item.operator) &&
        optional(item, "value", string) &&
        optional(item, "values", strings) &&
        optional(item, "valueNotice", string)
    )
  );
}

function criteria(value: unknown): boolean {
  return (
    filters(value) &&
    (value as Record<string, unknown>[]).every(
      (item) => Number.isInteger(item.id) && (item.id as number) >= 0
    )
  );
}

function columns(value: unknown): boolean {
  return (
    record(value) &&
    oneOf("all", "selected")(value.strategy) &&
    strings(value.names) &&
    optional(value, "touched", boolean) &&
    optional(value, "customOrder", boolean)
  );
}

function columnTypes(value: unknown): boolean {
  return (
    record(value) &&
    Object.values(value).every(oneOf("string", "number", "date", "datetime"))
  );
}

function preset(value: unknown): boolean {
  return (
    record(value) &&
    string(value.presetId) &&
    string(value.name) &&
    oneOf("filters", "columns", "both")(value.scope) &&
    Number.isInteger(value.revision) &&
    (value.revision as number) > 0 &&
    value.formatVersion === 1 &&
    timestamp(value.createdAt) &&
    timestamp(value.updatedAt) &&
    string(value.schemaFingerprint) &&
    columnTypes(value.schemaTypes) &&
    optional(value, "rows", oneOf("all", "filtered")) &&
    optional(value, "operation", oneOf("and", "or")) &&
    optional(value, "filters", filters) &&
    optional(
      value,
      "columns",
      (item) =>
        record(item) &&
        oneOf("all", "selected")(item.strategy) &&
        optional(item, "names", strings) &&
        (item.strategy === "all" || strings(item.names))
    )
  );
}

function configuration(value: unknown): boolean {
  return (
    record(value) &&
    oneOf("full", "filtered")(value.mode) &&
    criteria(value.criteria) &&
    oneOf("and", "or")(value.operation) &&
    columns(value.columns) &&
    optional(value, "appliedPreset", preset)
  );
}

function identity(value: unknown): boolean {
  return record(value) && string(value.identity) && string(value.id);
}

function requests(value: unknown): boolean {
  return (
    record(value) &&
    optional(value, "full", identity) &&
    optional(value, "filtered", identity)
  );
}

function report(value: unknown, product: OmsProduct): boolean {
  return (
    record(value) &&
    value.product === product &&
    string(value.snapshotId) &&
    strings(value.columns) &&
    timestamp(value.sourceRefreshedAt) &&
    timestamp(value.exportedAt) &&
    count(value.rowCount) &&
    count(value.totalBytes) &&
    optional(value, "columnTypes", columnTypes) &&
    optional(
      value,
      "columnValues",
      (choices) =>
        record(choices) &&
        Object.values(choices).every(
          (item) =>
            record(item) && strings(item.values) && boolean(item.complete)
        )
    ) &&
    Array.isArray(value.parts) &&
    value.parts.every(
      (item) =>
        record(item) &&
        string(item.id) &&
        string(item.fileName) &&
        count(item.rowCount) &&
        count(item.sizeBytes)
    )
  );
}

function editor(value: unknown, product: OmsProduct): boolean {
  return (
    record(value) &&
    oneOf("export", "edit", "save", "manage")(value.view) &&
    string(value.name) &&
    oneOf("filters", "columns", "both")(value.scope) &&
    optional(value, "updating", boolean) &&
    optional(value, "returnTo", oneOf("save", "actions", "edit")) &&
    optional(value, "cancelTo", oneOf("export", "manage")) &&
    optional(value, "previous", configuration) &&
    optional(value, "previousReport", (item) => report(item, product)) &&
    optional(value, "previousWorkspace", oneOf("rows", "columns")) &&
    optional(value, "previousRequests", requests) &&
    optional(
      value,
      "saveCheckpoint",
      (item) =>
        record(item) &&
        configuration(item.configuration) &&
        report(item.report, product) &&
        oneOf("rows", "columns")(item.workspace) &&
        requests(item.requests)
    )
  );
}

export function readOmsDraft(product: OmsProduct): OmsFilterDraft | undefined {
  try {
    const text = sessionStorage.getItem(storageKey(product));
    if (!text || text.length > limit) return undefined;
    const saved: unknown = JSON.parse(text);
    if (!record(saved) || saved.version !== 1 || !record(saved.draft))
      return undefined;
    const { draft } = saved;
    if (
      !report(draft.report, product) ||
      draft.snapshotId !==
        (draft.report as Record<string, unknown>).snapshotId ||
      !oneOf("full", "filtered")(draft.tab) ||
      !oneOf("and", "or")(draft.operation) ||
      !boolean(draft.editing) ||
      !criteria(draft.criteria) ||
      !columns(draft.columns) ||
      !optional(draft, "workspace", oneOf("rows", "columns")) ||
      !optional(draft, "columnsExpanded", boolean) ||
      !optional(draft, "appliedPreset", preset) ||
      !optional(draft, "undo", configuration) ||
      !optional(draft, "undoRequests", requests) ||
      !optional(
        draft,
        "viewingPrevious",
        (value) => record(value) && oneOf("full", "filtered")(value.mode)
      ) ||
      !optional(draft, "presetEditor", (value) => editor(value, product)) ||
      !optional(draft, "requests", requests) ||
      !optional(
        draft,
        "request",
        (value) =>
          identity(value) &&
          oneOf("full", "filtered")((value as Record<string, unknown>).mode)
      ) ||
      !optional(
        draft,
        "presetRequest",
        (value) =>
          identity(value) &&
          optional(value as Record<string, unknown>, "attemptedAt", count)
      )
    )
      return undefined;
    return draft as OmsFilterDraft;
  } catch {
    return undefined;
  }
}

export function writeOmsDraft(product: OmsProduct, draft: OmsFilterDraft) {
  try {
    const text = JSON.stringify({ version: 1, draft });
    if (text.length <= limit) sessionStorage.setItem(storageKey(product), text);
  } catch {
    // In-memory editing and accepted-job recovery still work without draft storage.
  }
}
